/**
 * ISG Pricing — Frontend Entry Point
 */

// --- Styles (from @isg/ui-kit) ---
import '@isg/ui-kit/css/tokens.css';
import '@isg/ui-kit/css/reset.css';
import '@isg/ui-kit/css/components.css';
import '@isg/ui-kit/css/layout.css';

// --- Services & components (from @isg/ui-kit) ---
import { router, store, auth , checkEnvBanner } from '@isg/ui-kit';
import { renderNavigation, renderMobileHeader, createSidebarOverlay } from '@isg/ui-kit';
import { applyBranding, loadBranding } from '@isg/ui-kit';

// Platform-admin Company switcher (no-op for non-admins).
import {
  mountCompanySwitcher, patchApiForCompanyOverride,
} from './company-switcher.js';

// Floating AI assistant (how-to for everyone, agentic for admins).
import { renderAIAssistant } from './components/ai-assistant-widget.js';

// Patch the api client BEFORE any page makes a call so an active
// override rides on every request from the first paint onwards.
patchApiForCompanyOverride();

/* SSO: capture handoff from intranet broker (same pattern as marketing) */
auth.captureSSOHandoff();

// ── Lazy-loaded pages ────────────────────────────────────────────────
const pages = {
  login:          () => import('./pages/login.js'),
  parts:          () => import('./pages/parts.js'),
  vendorAccounts: () => import('./pages/vendor-accounts.js'),
  partLists:        () => import('./pages/lists.js'),
  apiUsage:         () => import('./pages/api-usage.js'),
  rules:            () => import('./pages/rules.js'),
  recommendations:  () => import('./pages/recommendations.js'),
  publishingTargets:() => import('./pages/publishing-targets.js'),
  marginOverrides:  () => import('./pages/margin-overrides.js'),
  destinationRegions:() => import('./pages/destination-regions.js'),
  costRisk:         () => import('./pages/cost-risk.js'),
  scheduler:        () => import('./pages/scheduler.js'),
  priceHistory:     () => import('./pages/price-history.js'),
  competitorSnapshot:() => import('./pages/competitor-snapshot.js'),
  anomalies:        () => import('./pages/anomalies.js'),
  scalping:         () => import('./pages/scalping.js'),
  quotes:           () => import('./pages/quotes.js'),
  quoteHistory:     () => import('./pages/quote-history.js'),
  quotesWorkspace:  () => import('./pages/quotes-workspace.js'),
  quoteStrategies:  () => import('./pages/quote-strategies.js'),
  surgeStrategies:  () => import('./pages/surge-strategies.js'),
  uomReview:        () => import('./pages/uom-review.js'),
  winback:          () => import('./pages/winback.js'),
  enrichment:       () => import('./pages/enrichment.js'),
  // Phase 2+ pages slot in here:
  // tenants:         () => import('./pages/tenants.js'),
  // snapshots:       () => import('./pages/snapshots.js'),
  // scalping:        () => import('./pages/scalping.js'),
};

// ── Navigation config ────────────────────────────────────────────────
// Sections expand as features land. Phase 1 ships with Dashboard only.
const NAV_CONFIG = [
  {
    section: 'Catalogue',
    items: [
      { path: '/parts', label: 'Parts', icon: 'package' },
      // { path: '/snapshots', label: 'Vendor Snapshots', icon: 'database' },
    ],
  },
  {
    section: 'Pricing',
    items: [
      // R4 (2026-05-29): the standalone /recommendations nav item was
      // dropped — recommendations are now reached per-book from the
      // Price Book detail tabs (Phase 17.5b). The /recommendations
      // route stays registered below so deep links still resolve.
      { path: '/price-books', label: 'Price Books', icon: 'list' },
      { path: '/quotes', label: 'Quotes', icon: 'file-text' },
      { path: '/win-back', label: 'Business Win-back', icon: 'target' },
    ],
  },
  {
    section: 'Insights',
    items: [
      { path: '/price-history', label: 'Price History', icon: 'trending-up' },
      { path: '/competitor-snapshot', label: 'Competitor Snapshot', icon: 'columns' },
      { path: '/anomalies', label: 'Price Anomalies', icon: 'alert-triangle' },
      { path: '/scalping', label: 'Surge Pricing', icon: 'zap' },
    ],
  },
  {
    section: 'Admin',
    items: [
      { path: '/quote-strategies', label: 'Quote Strategies', icon: 'sliders', adminOnly: true },
      { path: '/surge-strategies', label: 'Surge Strategies', icon: 'zap', adminOnly: true },
      { path: '/uom-review', label: 'UoM Corrections', icon: 'ruler', adminOnly: true },
      { path: '/rules', label: 'Global Pricing Rules', icon: 'gauge', adminOnly: true },
      { path: '/margin-overrides', label: 'Margin Overrides', icon: 'percent', adminOnly: true },
      { path: '/destination-regions', label: 'Destination Regions', icon: 'globe', adminOnly: true },
      { path: '/cost-risk', label: 'Cost Risk', icon: 'alert-triangle', adminOnly: true },
      { path: '/vendor-accounts', label: 'Vendor Accounts', icon: 'key' },
      { path: '/api-usage', label: 'API Usage', icon: 'activity' },
      { path: '/scheduler', label: 'Sync Schedule', icon: 'refresh-cw' },
      { path: '/enrichment', label: 'E-com Enrichment', icon: 'image-plus' },
      { path: '/publishing-targets', label: 'Publishing Targets', icon: 'send' },
    ],
  },
  // Phase 2+ sections (currently stubbed):
  // {
  //   section: 'Pricing',
  //   items: [
  //     { path: '/recommendations', label: 'Recommendations', icon: 'trending-up' },
  //     { path: '/rules', label: 'Pricing Rules', icon: 'sliders' },
  //   ],
  // },
  // {
  //   section: 'Operations',
  //   items: [
  //     { path: '/scalping', label: 'Scalping', icon: 'zap' },
  //     { path: '/sync', label: 'Sync Schedule', icon: 'refresh-cw' },
  //   ],
  // },
  // {
  //   section: 'Admin',
  //   items: [
  //     { path: '/vendor-accounts', label: 'Vendor Accounts', icon: 'key' },
  //     { path: '/tenants', label: 'Tenant Settings', icon: 'building' },
  //   ],
  // },
];

// ── Render a page module into #main-content ──────────────────────────
async function renderPage(loader, params = {}) {
  const main = document.getElementById('main-content');
  if (!main) return;

  main.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;min-height:50vh">
      <div class="spinner"></div>
    </div>
  `;

  try {
    const module = await loader();
    main.innerHTML = '';
    await module.render(main, params);
  } catch (err) {
    console.error('Page render error:', err);
    main.innerHTML = `
      <div class="main-content-inner">
        <div class="empty-state">
          <p style="color: var(--red-600); font-weight: 500;">Something went wrong</p>
          <p>${err.message || 'Failed to load page.'}</p>
          <button class="btn btn-primary mt-4" onclick="location.reload()">Reload</button>
        </div>
      </div>
    `;
  }
}

// ── App shell ────────────────────────────────────────────────────────
function renderAppShell() {
  const app = document.getElementById('app');
  app.innerHTML = '';

  const stripe = document.createElement('div');
  stripe.className = 'accent-stripe';
  app.appendChild(stripe);

  const layout = document.createElement('div');
  layout.className = 'app-layout';

  const mobileHeader = renderMobileHeader({ appTitle: 'ISG Pricing' });
  const overlay = createSidebarOverlay();

  const sidebar = renderNavigation({
    navConfig: NAV_CONFIG,
    appTitle: 'ISG Pricing',
    fallbackInitial: 'P',
    appSlug: 'pricing',
  });

  const main = document.createElement('main');
  main.className = 'main-content';
  main.id = 'main-content';

  layout.appendChild(mobileHeader);
  layout.appendChild(overlay);
  layout.appendChild(sidebar);
  layout.appendChild(main);
  app.appendChild(layout);
}

// ── Init ─────────────────────────────────────────────────────────────
async function init() {
  await loadBranding().catch(() => {});
  const hasSession = await auth.checkSession();

  router
    .on('/', () => router.navigate('/parts'))
    .on('/login', (params) => renderPage(pages.login, params))
    .on('/parts', (params) => renderPage(pages.parts, params))
    .on('/vendor-accounts', (params) => renderPage(pages.vendorAccounts, params))
    .on('/part-lists', (params) => renderPage(pages.partLists, params))   // legacy alias
    .on('/price-books', (params) => renderPage(pages.partLists, params))
    .on('/api-usage', (params) => renderPage(pages.apiUsage, params))
    .on('/rules', (params) => renderPage(pages.rules, params))
    .on('/recommendations', (params) => renderPage(pages.recommendations, params))
    .on('/publishing-targets', (params) => renderPage(pages.publishingTargets, params))
    .on('/margin-overrides', (params) => renderPage(pages.marginOverrides, params))
    .on('/destination-regions', (params) => renderPage(pages.destinationRegions, params))
    .on('/cost-risk', (params) => renderPage(pages.costRisk, params))
    .on('/scheduler', (params) => renderPage(pages.scheduler, params))
    .on('/enrichment', (params) => renderPage(pages.enrichment, params))
    .on('/price-history', (params) => renderPage(pages.priceHistory, params))
    .on('/competitor-snapshot', (params) => renderPage(pages.competitorSnapshot, params))
    .on('/anomalies', (params) => renderPage(pages.anomalies, params))
    .on('/scalping', (params) => renderPage(pages.scalping, params))
    .on('/quotes', () => renderPage(pages.quotesWorkspace, { tab: 'build' }))
    .on('/quote-history', () => renderPage(pages.quotesWorkspace, { tab: 'history' }))
    .on('/win-back', (params) => renderPage(pages.winback, params))
    .on('/quote-strategies', (params) => renderPage(pages.quoteStrategies, params))
    .on('/surge-strategies', (params) => renderPage(pages.surgeStrategies, params))
    .on('/uom-review', () => renderPage(pages.uomReview));

  router.before((path) => {
    // An AUTHENTICATED user must never be left on the login page. Waving
    // /login through unconditionally renders the login FORM inside the app
    // shell — the app looks signed in and signed out at once, and only a
    // manual reload clears it. Not an edge case: api.js bounces here on any
    // 401 while the httpOnly cookie usually survives, so the reload lands
    // authenticated; the SSO broker's ?next and plain bookmarks reach it too.
    // Fixed in isg-app-template 2026-07-18; back-propagated 2026-07-31.
    if (path === '/login' && store.get('isAuthenticated')) {
      router.navigate('/');
      return false;
    }
    if (path === '/login') return true;
    if (!store.get('isAuthenticated')) {
      router.navigate('/login');
      return false;
    }
    return true;
  });

  router.after(() => {
    setTimeout(() => applyBranding(), 50);
  });

  if (!hasSession) {
    const app = document.getElementById('app');
    app.innerHTML = '<main id="main-content" role="main"></main>';
    const module = await pages.login();
    module.render(document.getElementById('main-content'));
    if (window.location.pathname !== '/login') {
      window.history.replaceState(null, '', '/login');
    }
    applyBranding();
  } else {
    renderAppShell();
    checkEnvBanner(); // beta/test banner (no-op on live)
    mountCompanySwitcher();
    await router.resolve();
    applyBranding();

    // Mount the AI assistant once — it floats on every page (its own
    // container; the widget owns the element's innerHTML).
    if (!document.getElementById('ai-assistant-mount')) {
      const aiMount = document.createElement('div');
      aiMount.id = 'ai-assistant-mount';
      document.body.appendChild(aiMount);
      renderAIAssistant(aiMount);
    }
  }
}

init().catch((err) => {
  console.error('Failed to initialize app:', err);
  document.getElementById('app').innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;min-height:100vh;flex-direction:column;gap:1rem;">
      <p>Failed to load the application. Please refresh.</p>
      <button class="btn btn-primary" onclick="location.reload()">Reload</button>
    </div>
  `;
});
